import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, GeoJSON, Marker, Popup } from 'react-leaflet';
import axios from 'axios';
import { API_URL } from '../components/config.js';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './ProjectsMapPage.css';

// Fix default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

/**
 * ProjectsMapPage Component
 * Shows all consultation projects with their locations on a map
 */
const ProjectsMapPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/projects`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      setProjects(response.data.projects || []);
      setError('');
    } catch (err) {
      console.error('Error loading projects:', err);
      setError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  // Parse mapData and filter projects that have valid location data
  const projectsWithLocations = projects.filter(project => {
    if (!project.mapData) return false;
    try {
      const parsed = JSON.parse(project.mapData);
      return parsed && (
        parsed.type === 'Point' || 
        parsed.type === 'Polygon' || 
        parsed.type === 'LineString' || 
        parsed.type === 'MultiPolygon' ||
        parsed.type === 'MultiPoint' ||
        parsed.type === 'MultiLineString' ||
        parsed.type === 'GeometryCollection'
      );
    } catch (e) {
      return false;
    }
  });

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#0F9D58';
      case 'pending': return '#F4B400';
      case 'completed': return '#4285F4';
      case 'draft': return '#6c757d';
      default: return '#999';
    }
  };

  // Style function for GeoJSON features
  const getFeatureStyle = (project) => ({
    color: getStatusColor(project.status),
    weight: 3,
    opacity: 0.8,
    fillOpacity: 0.35
  });

  if (loading) {
    return (
      <div className="projects-map-page">
        <div className="page-header">
          <h2>📋 Consultation Projects Map</h2>
        </div>
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading consultation projects...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="projects-map-page">
        <div className="page-header">
          <h2>📋 Consultation Projects Map</h2>
        </div>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="projects-map-page">
      <div className="page-header">
        <h2>📋 Consultation Projects Map</h2>
        <p className="page-description">
          View all consultation projects across RSMIN traditional territory.
          See project locations, status, and details.
        </p>
      </div>

      {/* Info Stats */}
      <div className="map-stats">
        <div className="stat-card">
          <span className="stat-number">{projects.length}</span>
          <span className="stat-label">Total Projects</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{projectsWithLocations.length}</span>
          <span className="stat-label">Projects with Locations</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">
            {projects.filter(p => p.status === 'active').length}
          </span>
          <span className="stat-label">Active Projects</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">
            {projects.filter(p => p.isPubliclyVisible).length}
          </span>
          <span className="stat-label">Public Projects</span>
        </div>
      </div>

      {/* Map */}
      <div className="map-container">
        {projectsWithLocations.length > 0 ? (
          <MapContainer
            center={[48.3809, -89.2477]} // Thunder Bay
            zoom={10}
            style={{ height: '600px', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            {projectsWithLocations.map((project) => {
              try {
                const geometry = JSON.parse(project.mapData);
                
                return (
                  <GeoJSON
                    key={project._id}
                    data={{
                      type: 'Feature',
                      geometry: geometry,
                      properties: {
                        name: project.name,
                        status: project.status
                      }
                    }}
                    style={getFeatureStyle(project)}
                  >
                    <Popup maxWidth={300}>
                      <div className="project-popup">
                        <h4>{project.name}</h4>
                        {project.description && (
                          <p className="description">
                            {project.description.length > 120 
                              ? project.description.substring(0, 120) + '...' 
                              : project.description}
                          </p>
                        )}
                        <button 
                          className="comment-button"
                          onClick={() => window.location.href = `/consultation/project/${project._id}`}
                        >
                          💬 View & Comment
                        </button>
                      </div>
                    </Popup>
                  </GeoJSON>
                );
              } catch (err) {
                console.error('Error rendering project:', project.name, err);
                return null;
              }
            })}
          </MapContainer>
        ) : (
          <div className="no-data-message">
            <p>📍 No projects with location data yet.</p>
            <p>Create a project and add a location to see it on the map!</p>
          </div>
        )}
      </div>

      <div className="consultation-info">
        <h3>About Consultation Projects</h3>
        <p>
          RSMIN actively monitors and participates in consultation projects that may impact
          traditional territory, treaty rights, and community interests. This map shows 
          projects that require community input and engagement.
        </p>
        <div className="consultation-guidelines">
          <h4>How to Participate</h4>
          <ul>
            <li>📍 Click on map markers to see project details</li>
            <li>💬 Submit comments and concerns on active projects</li>
            <li>📅 Check project timelines and deadlines for input</li>
            <li>📧 Contact RSMIN staff for more information</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ProjectsMapPage;
